import { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Send, Mail, Phone, Eye, Search, ChevronRight, ChevronDown } from 'lucide-react';

import { useApi } from '../../lib/api.js';
import { useCurrentUser } from '../../hooks/useCurrentUser.js';
import { PageHeader } from '../../components/ui/Feedback.jsx';
import { Button } from '../../components/ui/Button.jsx';
import { Modal } from '../../components/ui/Modal.jsx';
import { Input, Textarea } from '../../components/ui/Form.jsx';

// =============================================================================
// MessagesPage — group memos / notices to chauffeurs (owner only)
// =============================================================================
// Tabs:
//   New message — pick recipients, write email and/or SMS, preview, send
//   Sent        — history with per-recipient delivery status
//
// Backend: routes/messages.js
// =============================================================================

const TYPE_OPTIONS = [
    { key: 'direct',  label: 'Direct chauffeurs' },
    { key: 'partner', label: 'Fleet partners' },
    { key: 'fleet',   label: 'Under a partner' },
];

function smsSegments(len) {
    if (len === 0) return 0;
    if (len <= 160) return 1;
    return Math.ceil(len / 153);
}

function formatWhen(ts) {
    if (!ts) return '';
    return new Date(ts).toLocaleString('en-GB', {
        day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
    });
}

export default function MessagesPage() {
    const { isOwner } = useCurrentUser();
    const [tab, setTab] = useState('new');

    if (!isOwner) {
        return <div className="p-6 text-sm text-ink-subtle">Messages are owner-only.</div>;
    }

    return (
        <div>
            <PageHeader title="Messages" subtitle="Send a memo or notice to your chauffeurs by email and/or SMS." />

            <div className="inline-flex p-1 bg-canvas rounded-lg mb-5">
                {[{ v: 'new', l: 'New message' }, { v: 'sent', l: 'Sent' }].map((t) => (
                    <button
                        key={t.v}
                        type="button"
                        onClick={() => setTab(t.v)}
                        className={`px-4 py-1.5 text-sm font-medium rounded transition-colors ${
                            tab === t.v ? 'bg-paper text-ink shadow-sm' : 'text-ink-subtle hover:text-ink'
                        }`}
                    >
                        {t.l}
                    </button>
                ))}
            </div>

            {tab === 'new' ? <NewMessage onSent={() => setTab('sent')} /> : <SentMessages />}
        </div>
    );
}

// =============================================================================
// New message
// =============================================================================
function NewMessage({ onSent }) {
    const api = useApi();
    const queryClient = useQueryClient();

    const recipientsQ = useQuery({
        queryKey: ['messages', 'recipients', 'chauffeurs'],
        queryFn: () => api.get('/messages/recipients?audience=chauffeurs'),
    });
    const all = recipientsQ.data?.recipients || [];

    const [types, setTypes] = useState(() => new Set(['direct', 'partner']));
    const [area, setArea] = useState('');
    const [search, setSearch] = useState('');
    const [excluded, setExcluded] = useState(() => new Set());

    const [sendEmail, setSendEmail] = useState(true);
    const [sendSms, setSendSms] = useState(false);
    const [subject, setSubject] = useState('');
    const [body, setBody] = useState('Hi {first_name},\n\n');
    const [smsBody, setSmsBody] = useState('Lanz CTS: ');

    const [preview, setPreview] = useState(null); // { subject, html }
    const [confirming, setConfirming] = useState(false);
    const [result, setResult] = useState(null);

    const areaOptions = useMemo(() => {
        const s = new Set();
        all.forEach((r) => (r.service_areas || []).forEach((a) => s.add(a)));
        return [...s].sort();
    }, [all]);

    // Pool = everyone matching type + area. Selected = pool minus unticked.
    const pool = useMemo(() => all.filter((r) =>
        types.has(r.type) && (!area || (r.service_areas || []).includes(area))
    ), [all, types, area]);

    const visible = useMemo(() => {
        const q = search.trim().toLowerCase();
        if (!q) return pool;
        return pool.filter((r) =>
            [r.name, r.call_sign, r.email, r.phone, r.company].filter(Boolean)
                .some((v) => String(v).toLowerCase().includes(q))
        );
    }, [pool, search]);

    const selected = pool.filter((r) => !excluded.has(r.id));
    const noEmail = sendEmail ? selected.filter((r) => !r.email) : [];
    const noPhone = sendSms ? selected.filter((r) => !r.phone) : [];

    function toggleType(key) {
        setTypes((prev) => {
            const next = new Set(prev);
            if (next.has(key)) next.delete(key); else next.add(key);
            return next;
        });
    }
    function toggleRecipient(id) {
        setExcluded((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id); else next.add(id);
            return next;
        });
    }

    const smsLen = smsBody.trim().length;
    const canSend = selected.length > 0
        && (sendEmail || sendSms)
        && (!sendEmail || (subject.trim() && body.trim()))
        && (!sendSms || smsLen > 0);

    const previewMutation = useMutation({
        mutationFn: () => api.post('/messages/preview', {
            subject, body, sample_first_name: selected[0]?.first_name || 'John',
        }),
        onSuccess: (data) => setPreview(data),
        onError: (err) => toast.error(err?.message || 'Could not build preview'),
    });

    const sendMutation = useMutation({
        mutationFn: () => api.post('/messages', {
            audience: 'chauffeurs',
            recipient_ids: selected.map((r) => r.id),
            send_email: sendEmail,
            send_sms: sendSms,
            subject: subject.trim(),
            body,
            sms_body: smsBody.trim(),
        }),
        onSuccess: (data) => {
            setConfirming(false);
            setResult(data);
            queryClient.invalidateQueries({ queryKey: ['messages', 'history'] });
        },
        onError: (err) => {
            setConfirming(false);
            toast.error(err?.message || 'Send failed');
        },
    });

    if (result) {
        const s = result.summary || {};
        const problems = (result.results || []).filter((r) =>
            ['failed', 'skipped'].includes(r.email_status) || ['failed', 'skipped'].includes(r.sms_status));
        return (
            <div className="card">
                <div className="card-body space-y-4">
                    <h2 className="text-base font-semibold text-ink">Message sent</h2>
                    <div className="text-sm text-ink space-y-1">
                        {sendEmail && <div>Email: <strong>{s.email_sent}</strong> sent{s.email_failed ? `, ${s.email_failed} failed` : ''}{s.email_skipped ? `, ${s.email_skipped} skipped` : ''}</div>}
                        {sendSms && <div>SMS: <strong>{s.sms_sent}</strong> sent{s.sms_failed ? `, ${s.sms_failed} failed` : ''}{s.sms_skipped ? `, ${s.sms_skipped} skipped` : ''}</div>}
                    </div>
                    {problems.length > 0 && (
                        <div className="text-xs bg-amber-50 border border-amber-200 rounded px-3 py-2 space-y-1">
                            {problems.map((p) => (
                                <div key={p.driver_id} className="text-amber-900">
                                    <strong>{p.name}</strong>
                                    {p.email_error ? ` · email: ${p.email_error}` : ''}
                                    {p.sms_error ? ` · SMS: ${p.sms_error}` : ''}
                                </div>
                            ))}
                        </div>
                    )}
                    <div className="flex gap-2">
                        <Button variant="secondary" onClick={() => setResult(null)}>Write another</Button>
                        <Button variant="primary" onClick={onSent}>View sent messages</Button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-5">
            {/* ---- Recipients ---- */}
            <div className="lg:col-span-2 card">
                <div className="card-header">
                    <h2 className="text-sm font-semibold text-ink">
                        Recipients · {selected.length} selected
                    </h2>
                </div>
                <div className="card-body space-y-3">
                    <div className="flex flex-wrap gap-1.5">
                        {TYPE_OPTIONS.map((t) => {
                            const on = types.has(t.key);
                            return (
                                <button
                                    key={t.key}
                                    type="button"
                                    onClick={() => toggleType(t.key)}
                                    className={`px-2.5 py-1 text-xs rounded-md border transition-colors ${
                                        on ? 'bg-ink text-paper border-ink' : 'bg-paper text-ink-subtle border-paper-border hover:text-ink'
                                    }`}
                                >
                                    {t.label}
                                </button>
                            );
                        })}
                    </div>

                    {areaOptions.length > 0 && (
                        <select
                            value={area}
                            onChange={(e) => setArea(e.target.value)}
                            className="w-full text-sm px-2 py-1.5 border border-paper-border rounded bg-paper text-ink"
                        >
                            <option value="">All service areas</option>
                            {areaOptions.map((a) => <option key={a} value={a}>{a}</option>)}
                        </select>
                    )}

                    <div className="relative">
                        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-ink-subtle pointer-events-none" />
                        <input
                            type="search"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            placeholder="Search name, call sign…"
                            className="w-full pl-8 pr-2 py-1.5 text-sm border border-paper-border rounded bg-paper text-ink"
                        />
                    </div>

                    <div className="flex gap-3 text-xs">
                        <button type="button" className="text-ink-info hover:underline"
                            onClick={() => setExcluded((prev) => {
                                const next = new Set(prev);
                                visible.forEach((r) => next.delete(r.id));
                                return next;
                            })}>
                            Tick all shown
                        </button>
                        <button type="button" className="text-ink-info hover:underline"
                            onClick={() => setExcluded((prev) => {
                                const next = new Set(prev);
                                visible.forEach((r) => next.add(r.id));
                                return next;
                            })}>
                            Untick all shown
                        </button>
                    </div>

                    {recipientsQ.isLoading ? (
                        <p className="text-sm text-ink-subtle italic">Loading…</p>
                    ) : recipientsQ.isError ? (
                        <p className="text-sm text-red-700">{recipientsQ.error?.message || 'Could not load chauffeurs'}</p>
                    ) : visible.length === 0 ? (
                        <p className="text-sm text-ink-subtle italic">No chauffeurs match.</p>
                    ) : (
                        <div className="border border-paper-border-soft rounded-md divide-y divide-paper-border-soft max-h-[28rem] overflow-y-auto">
                            {visible.map((r) => (
                                <label key={r.id} className="flex items-start gap-2.5 px-3 py-2 cursor-pointer hover:bg-canvas/40">
                                    <input
                                        type="checkbox"
                                        checked={!excluded.has(r.id)}
                                        onChange={() => toggleRecipient(r.id)}
                                        className="mt-0.5"
                                    />
                                    <div className="min-w-0 flex-1">
                                        <div className="text-sm text-ink flex items-center gap-2 flex-wrap">
                                            <span className="font-medium">{r.name}</span>
                                            {r.call_sign && <span className="text-xs font-mono text-ink-subtle">{r.call_sign}</span>}
                                        </div>
                                        <div className="text-xs text-ink-subtle flex items-center gap-2 flex-wrap mt-0.5">
                                            {r.company && <span>{r.type === 'fleet' ? `Under ${r.company}` : r.company}</span>}
                                            <span className={`inline-flex items-center gap-1 ${sendEmail && !r.email ? 'text-amber-700' : ''}`}>
                                                <Mail className="h-3 w-3" />{r.email ? 'email' : 'no email'}
                                            </span>
                                            <span className={`inline-flex items-center gap-1 ${sendSms && !r.phone ? 'text-amber-700' : ''}`}>
                                                <Phone className="h-3 w-3" />{r.phone ? 'phone' : 'no phone'}
                                            </span>
                                        </div>
                                    </div>
                                </label>
                            ))}
                        </div>
                    )}
                </div>
            </div>

            {/* ---- Compose ---- */}
            <div className="lg:col-span-3 card">
                <div className="card-header">
                    <h2 className="text-sm font-semibold text-ink">Message</h2>
                </div>
                <div className="card-body space-y-5">
                    <div className="flex gap-4">
                        <label className="inline-flex items-center gap-2 text-sm text-ink cursor-pointer">
                            <input type="checkbox" checked={sendEmail} onChange={(e) => setSendEmail(e.target.checked)} />
                            Email
                        </label>
                        <label className="inline-flex items-center gap-2 text-sm text-ink cursor-pointer">
                            <input type="checkbox" checked={sendSms} onChange={(e) => setSendSms(e.target.checked)} />
                            SMS
                        </label>
                    </div>

                    {sendEmail && (
                        <div className="space-y-3">
                            <div>
                                <label className="block text-xs uppercase tracking-wide text-ink-subtle font-medium mb-1">Subject</label>
                                <Input
                                    value={subject}
                                    onChange={(e) => setSubject(e.target.value)}
                                    placeholder="e.g. Updated payment structure"
                                    maxLength={200}
                                />
                            </div>
                            <div>
                                <label className="block text-xs uppercase tracking-wide text-ink-subtle font-medium mb-1">Email message</label>
                                <Textarea
                                    value={body}
                                    onChange={(e) => setBody(e.target.value)}
                                    rows={14}
                                />
                                <p className="text-xs text-ink-subtle mt-1">
                                    <code>{'{first_name}'}</code> becomes each chauffeur's first name. Blank line = new paragraph.
                                    Lines starting with <code>- </code> become bullet points. <code>**text**</code> = bold.
                                </p>
                            </div>
                            <Button
                                variant="secondary"
                                onClick={() => previewMutation.mutate()}
                                loading={previewMutation.isPending}
                                disabled={!subject.trim() && !body.trim()}
                            >
                                <Eye className="h-4 w-4" />
                                Preview email
                            </Button>
                        </div>
                    )}

                    {sendSms && (
                        <div>
                            <label className="block text-xs uppercase tracking-wide text-ink-subtle font-medium mb-1">SMS text</label>
                            <Textarea
                                value={smsBody}
                                onChange={(e) => setSmsBody(e.target.value)}
                                rows={3}
                                maxLength={640}
                            />
                            <p className={`text-xs mt-1 ${smsSegments(smsLen) > 1 ? 'text-amber-700' : 'text-ink-subtle'}`}>
                                {smsLen} characters · {smsSegments(smsLen)} SMS per person
                                {smsSegments(smsLen) > 1 ? ' (each extra part is charged)' : ''}. Keep it short; put the detail in the email.
                            </p>
                        </div>
                    )}

                    {(noEmail.length > 0 || noPhone.length > 0) && (
                        <div className="text-xs bg-amber-50 border border-amber-200 text-amber-900 rounded px-3 py-2 space-y-0.5">
                            {noEmail.length > 0 && <div>{noEmail.length} selected without an email: {noEmail.map((r) => r.name).join(', ')}</div>}
                            {noPhone.length > 0 && <div>{noPhone.length} selected without a phone: {noPhone.map((r) => r.name).join(', ')}</div>}
                        </div>
                    )}

                    <div className="pt-3 border-t border-paper-border-soft flex justify-end">
                        <Button variant="primary" onClick={() => setConfirming(true)} disabled={!canSend}>
                            <Send className="h-4 w-4" />
                            Send to {selected.length} chauffeur{selected.length === 1 ? '' : 's'}
                        </Button>
                    </div>
                </div>
            </div>

            {preview && (
                <Modal open onClose={() => setPreview(null)} title={preview.subject} description="Preview, using the first selected chauffeur's name." size="lg">
                    <iframe
                        title="Email preview"
                        srcDoc={preview.html}
                        className="w-full h-[70vh] border border-paper-border rounded"
                    />
                    <div className="mt-4 flex justify-end">
                        <Button variant="secondary" onClick={() => setPreview(null)}>Close</Button>
                    </div>
                </Modal>
            )}

            {confirming && (
                <Modal
                    open
                    onClose={sendMutation.isPending ? undefined : () => setConfirming(false)}
                    title="Send message?"
                    description={`To ${selected.length} chauffeur${selected.length === 1 ? '' : 's'} by ${[sendEmail && 'email', sendSms && 'SMS'].filter(Boolean).join(' and ')}. This can't be unsent.`}
                    size="md"
                >
                    {sendMutation.isPending && (
                        <p className="text-sm text-ink-subtle mt-2">Sending… this takes about a second per chauffeur. Keep this page open.</p>
                    )}
                    <div className="mt-6 flex items-center justify-end gap-2 pt-4 border-t border-paper-border-soft">
                        <Button variant="secondary" onClick={() => setConfirming(false)} disabled={sendMutation.isPending}>
                            Cancel
                        </Button>
                        <Button variant="primary" onClick={() => sendMutation.mutate()} loading={sendMutation.isPending}>
                            <Send className="h-4 w-4" />
                            Send now
                        </Button>
                    </div>
                </Modal>
            )}
        </div>
    );
}

// =============================================================================
// Sent
// =============================================================================
function SentMessages() {
    const api = useApi();
    const [openId, setOpenId] = useState(null);

    const historyQ = useQuery({
        queryKey: ['messages', 'history'],
        queryFn: () => api.get('/messages'),
    });
    const messages = historyQ.data?.messages || [];

    if (historyQ.isLoading) return <p className="text-sm text-ink-subtle italic">Loading…</p>;
    if (messages.length === 0) {
        return <div className="card"><div className="card-body text-sm text-ink-subtle italic">Nothing sent yet.</div></div>;
    }

    return (
        <div className="card divide-y divide-paper-border-soft">
            {messages.map((m) => (
                <div key={m.id}>
                    <button
                        type="button"
                        onClick={() => setOpenId(openId === m.id ? null : m.id)}
                        className="w-full text-left px-5 py-3 flex items-start justify-between gap-4 hover:bg-canvas/40"
                    >
                        <div className="min-w-0 flex-1">
                            <div className="text-sm font-medium text-ink truncate">
                                {m.subject || m.sms_body || '(no subject)'}
                            </div>
                            <div className="text-xs text-ink-subtle mt-0.5">
                                {formatWhen(m.created_at)} · {m.recipient_count} chauffeur{m.recipient_count === 1 ? '' : 's'}
                                {m.send_email && ` · ${m.email_sent} emailed`}
                                {m.send_sms && ` · ${m.sms_sent} texted`}
                                {m.problems > 0 && <span className="text-amber-700"> · {m.problems} with problems</span>}
                            </div>
                        </div>
                        {openId === m.id
                            ? <ChevronDown className="h-4 w-4 text-ink-faint flex-shrink-0 mt-0.5" />
                            : <ChevronRight className="h-4 w-4 text-ink-faint flex-shrink-0 mt-0.5" />}
                    </button>
                    {openId === m.id && <MessageDetail id={m.id} />}
                </div>
            ))}
        </div>
    );
}

function MessageDetail({ id }) {
    const api = useApi();
    const q = useQuery({
        queryKey: ['messages', 'detail', id],
        queryFn: () => api.get(`/messages/${id}`),
    });
    if (q.isLoading) return <p className="px-5 pb-4 text-sm text-ink-subtle italic">Loading…</p>;
    const m = q.data?.message;
    const recipients = q.data?.recipients || [];

    const statusClass = (s) => s === 'sent' ? 'text-green-700' : s === 'failed' ? 'text-red-700' : 'text-amber-700';

    return (
        <div className="px-5 pb-5 space-y-4 bg-canvas/30">
            {m?.body && (
                <div className="text-sm text-ink whitespace-pre-wrap bg-paper border border-paper-border-soft rounded p-3">{m.body}</div>
            )}
            {m?.sms_body && (
                <div className="text-sm text-ink bg-paper border border-paper-border-soft rounded p-3">
                    <span className="text-xs uppercase tracking-wide text-ink-subtle font-medium">SMS · </span>{m.sms_body}
                </div>
            )}
            <table className="w-full text-sm">
                <thead>
                    <tr className="text-xs uppercase tracking-wide text-ink-subtle">
                        <th className="text-left font-medium pb-2">Chauffeur</th>
                        {m?.send_email && <th className="text-left font-medium pb-2">Email</th>}
                        {m?.send_sms && <th className="text-left font-medium pb-2">SMS</th>}
                    </tr>
                </thead>
                <tbody>
                    {recipients.map((r) => (
                        <tr key={r.id} className="border-t border-paper-border-soft align-top">
                            <td className="py-1.5 text-ink">{r.name}</td>
                            {m?.send_email && (
                                <td className={`py-1.5 ${statusClass(r.email_status)}`}>
                                    {r.email_status}{r.email_error ? ` · ${r.email_error}` : ''}
                                </td>
                            )}
                            {m?.send_sms && (
                                <td className={`py-1.5 ${statusClass(r.sms_status)}`}>
                                    {r.sms_status}{r.sms_error ? ` · ${r.sms_error}` : ''}
                                </td>
                            )}
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}
